import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Settings as SettingsIcon, 
  Printer, 
  Save,
  Globe,
  Receipt,
  Percent,
  Building2,
  Loader2,
  Check,
  Upload,
  X,
  Eye,
  Image as ImageIcon,
  Moon,
  Sun,
  ScanBarcode,
  MonitorSmartphone,
  Copy,
  CreditCard,
  ChevronRight,
  AlertCircle,
  TestTube2,
  FileText,
  LayoutList,
  Coins
} from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "@/contexts/SettingsContext";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useScannerListener } from "@/hooks/useScannerListener";
import { printTestTicket, generateTicketHTML } from "@/lib/ticketGenerator";
import { CURRENCIES, formatCurrency } from "@/lib/currency";
import { Checkbox } from "@/components/ui/checkbox";
import { MENU_ITEMS, SECTION_LABELS, SECTION_ORDER, isItemAllowedForRole } from "@/config/menuCatalog";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { user, updateUser, updateMenuPreferences } = useAuth();
  const navigate = useNavigate();
  const { 
    darkMode, setDarkMode,
    language, setLanguage, 
    autoPrint, setAutoPrint, 
    t,
    companyLogo, setCompanyLogo,
    ticketHeader, setTicketHeader,
    ticketFooter, setTicketFooter,
    ticketTerms, setTicketTerms,
    showDniOnTicket, setShowDniOnTicket,
    showVatOnTicket, setShowVatOnTicket,
    defaultVat, setDefaultVat,
    vatIncludedInPrices, setVatIncludedInPrices,
    calculateVat,
    currency, setCurrency,
    // Hardware
    quickScanMode, setQuickScanMode,
    paperWidth, setPaperWidth,
    autoPrintOnPayment, setAutoPrintOnPayment,
    printDoubleCopy, setPrintDoubleCopy,
    printMode, setPrintMode,
    qzPrinterName, setQzPrinterName,
    saveSettingsToBackend
  } = useSettings();
  
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const fileInputRef = useRef(null);
  const [storeInfo, setStoreInfo] = useState(null);

  // ── Hardware test dialogs ──────────────────────────────────────────────
  const [scannerTestOpen, setScannerTestOpen] = useState(false);
  const [scannerTestCode, setScannerTestCode] = useState("");
  // A dedicated listener only when the test dialog is open so it doesn't
  // interfere with the global one.
  useScannerListener({
    onScan: (code) => setScannerTestCode(code),
    enabled: scannerTestOpen,
    minLength: 3,
    autoFocus: false,
  });

  // ── QZ Tray (thermal ESC/POS) ─────────────────────────────────────────
  const [qzPrinters, setQzPrinters] = useState([]);
  const [qzStatus, setQzStatus] = useState("idle"); // idle | detecting | ok | missing

  const detectQZ = async () => {
    setQzStatus("detecting");
    try {
      const svc = await import("@/lib/escposService");
      const ok = await svc.isQZAvailable();
      if (!ok) {
        setQzStatus("missing");
        setQzPrinters([]);
        return false;
      }
      const printers = await svc.listQZPrinters();
      setQzPrinters(printers);
      setQzStatus("ok");
      if (!qzPrinterName && printers[0]) setQzPrinterName(printers[0]);
      return true;
    } catch {
      setQzStatus("missing");
      return false;
    }
  };

  // Auto-detect QZ Tray the first time the operator switches to escpos mode.
  useEffect(() => {
    if (printMode === "escpos" && qzStatus === "idle") {
      detectQZ();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printMode]);

  const handlePrintTestTicket = async () => {
    try {
      // Persist paperWidth first so the test uses the exact selected value.
      await saveSettingsToBackend?.();
    } catch {
      // best-effort — printTestTicket falls back to localStorage
    }
    printTestTicket();
    toast.success(`Ticket de prueba enviado a la impresora (${paperWidth})`);
  };

  // ── Live ticket preview ────────────────────────────────────────────────
  // Re-generates the ticket HTML every time the paper width, header,
  // footer, terms or logo change — so the counter sees EXACTLY what will
  // come out of the printer, without wasting paper.
  const previewSrcDoc = useMemo(() => {
    try {
      return generateTicketHTML({
        ticketType: "rental",
        data: {
          operationNumber: "PREVIEW-0001",
          date: new Date().toISOString(),
          customer: "Juan García López",
          dni: "12345678A",
          startDate: new Date().toISOString(),
          endDate: new Date(Date.now() + 86400000).toISOString(),
          days: 1,
          items: [
            { item_type: "Esquís Salomon X-Max 170", price: 35, quantity: 1 },
            { item_type: "Botas Esquí 42", price: 15, quantity: 1 },
            { item_type: "Casco Adulto", price: 8, quantity: 1 },
          ],
          total: 58,
          paymentMethod: "cash",
          deposit: 0,
          pendingAmount: 0,
        },
        settings: {
          companyLogo,
          ticketHeader,
          ticketFooter,
          ticketTerms,
          showDniOnTicket,
          showVatOnTicket,
          defaultVat,
          vatIncludedInPrices,
          language,
          paperWidth,
        },
      });
    } catch {
      return "";
    }
  }, [paperWidth, ticketHeader, ticketFooter, ticketTerms, companyLogo, showDniOnTicket, showVatOnTicket, defaultVat, vatIncludedInPrices, language, currency]);

  // Load store info
  useEffect(() => {
    const loadStoreInfo = async () => {
      if (user?.role === "super_admin") {
        setStoreInfo({ name: "SUPER ADMIN", id: "N/A", status: "global" });
        return;
      }
      
      // Decode JWT to get store_id
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.store_id) {
            const response = await axios.get(`${API}/api/stores/${payload.store_id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            setStoreInfo(response.data);
          }
        }
      } catch (error) {
        console.error("Error loading store info:", error);
      }
    };
    
    loadStoreInfo();
  }, [user]);

  // Sample data for ticket preview
  const sampleTicket = {
    date: new Date().toLocaleDateString('es-ES'),
    customer: "Juan García López",
    dni: "12345678A",
    items: [
      { name: "Esquís Salomon X-Max 170", price: 35.00 },
      { name: "Botas Esquí 42", price: 15.00 },
      { name: "Casco Adulto", price: 8.00 }
    ],
    paymentMethod: "cash",
    total: 58.00
  };

  const vatCalc = calculateVat(sampleTicket.total);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setHasChanges(true);
    toast.success(lang === 'es' ? 'Idioma cambiado a Español' : 'Language changed to English');
  };

  const handleDarkModeChange = (enabled) => {
    setDarkMode(enabled);
    setHasChanges(true);
    toast.success(enabled 
      ? (language === 'es' ? 'Modo Oscuro activado' : 'Dark Mode enabled')
      : (language === 'es' ? 'Modo Claro activado' : 'Light Mode enabled')
    );
  };

  const handleAutoPrintChange = (enabled) => {
    // Hardware toggle — apply live, no "Save" click required.
    // The debounced sync in SettingsContext persists the new value.
    setAutoPrint(enabled);
  };

  // Función para comprimir imagen a Base64 con tamaño máximo
  const compressImage = (file, maxWidth = 500, maxHeight = 500, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calcular dimensiones manteniendo proporción
          let { width, height } = img;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
          
          // Crear canvas y dibujar imagen redimensionada
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convertir a Base64 con compresión
          const base64 = canvas.toDataURL('image/jpeg', quality);
          
          // Verificar tamaño final (máximo 200KB para tickets)
          const sizeKB = (base64.length * 0.75) / 1024;
          if (sizeKB > 200) {
            // Reducir calidad si es muy grande
            const reducedBase64 = canvas.toDataURL('image/jpeg', 0.5);
            resolve(reducedBase64);
          } else {
            resolve(base64);
          }
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      toast.error("Por favor, selecciona un archivo de imagen válido");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo es demasiado grande. Máximo 5MB.");
      return;
    }

    try {
      toast.loading("Procesando imagen...", { id: 'logo-upload' });
      
      // Comprimir la imagen antes de guardar
      const compressedBase64 = await compressImage(file, 500, 500, 0.8);
      
      // Guardar en el estado (se sincronizará con localStorage vía useEffect)
      setCompanyLogo(compressedBase64);
      setHasChanges(true);
      
      const sizeKB = Math.round((compressedBase64.length * 0.75) / 1024);
      toast.success(`Logo actualizado (${sizeKB}KB)`, { id: 'logo-upload' });
      
    } catch (error) {
      console.error("Error procesando imagen:", error);
      toast.error("Error al procesar la imagen", { id: 'logo-upload' });
    }
  };

  const handleRemoveLogo = () => {
    setCompanyLogo(null);
    setHasChanges(true);
    toast.success("Logo eliminado");
  };

  const handleTicketFieldChange = (setter) => (e) => {
    setter(e.target.value);
    setHasChanges(true);
  };

  const handleToggleChange = (setter) => (value) => {
    setter(value);
    setHasChanges(true);
  };

  const handleVatChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setDefaultVat(Math.max(0, Math.min(100, value)));
    setHasChanges(true);
  };

  const saveAllSettings = async () => {
    setSaving(true);
    try {
      const saved = await saveSettingsToBackend();
      if (saved) {
        // Update sidebar logo immediately
        updateUser({ store_logo: companyLogo || "" });
        toast.success(t('settings.saved'));
        setHasChanges(false);
      } else {
        toast.error("Error al guardar la configuración");
      }
    } catch (error) {
      toast.error("Error al guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`min-h-screen p-6 lg:p-8 transition-colors duration-300 ${darkMode ? 'bg-slate-900' : 'bg-slate-50'}`} data-testid="settings-page">
      {/* Header with Save Button */}
      <div className="flex items-center justify-between mb-8 sticky top-0 z-10 py-4 -mt-4 -mx-6 px-6 lg:-mx-8 lg:px-8 backdrop-blur-sm" style={{ backgroundColor: darkMode ? 'rgba(15, 23, 42, 0.9)' : 'rgba(248, 250, 252, 0.9)' }}>
        <div>
          <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Plus Jakarta Sans' }}>
            <SettingsIcon className="inline-block h-8 w-8 mr-3 text-primary" />
            {t('settings.title')}
          </h1>
          <p className={`mt-1 ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
            {t('settings.subtitle')}
          </p>
        </div>
        <Button 
          onClick={saveAllSettings} 
          disabled={saving || !hasChanges}
          className={`min-w-[180px] ${hasChanges ? 'bg-primary hover:bg-primary/90' : ''}`}
          size="lg"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : hasChanges ? (
            <Save className="h-4 w-4 mr-2" />
          ) : (
            <Check className="h-4 w-4 mr-2" />
          )}
          {saving ? t('common.loading') : hasChanges ? t('settings.saveChanges') : 'Guardado'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Quick Access - Billing (Admin Only) */}
        {(user?.role === "admin" || user?.role === "super_admin") && (
          <div className="lg:col-span-2">
            <Card 
              className={`cursor-pointer transition-all hover:shadow-lg ${
                darkMode 
                  ? 'bg-gradient-to-r from-emerald-900/20 to-teal-900/20 border-emerald-500/50 hover:border-emerald-400' 
                  : 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 hover:border-emerald-400'
              }`}
              onClick={() => navigate("/facturacion")}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-emerald-500/20' : 'bg-emerald-500/20'}`}>
                      <CreditCard className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        Facturación
                      </h3>
                      <p className={`text-sm ${darkMode ? 'text-slate-600' : 'text-slate-600'}`}>
                        Gestiona tus datos fiscales y descarga tus facturas
                      </p>
                    </div>
                  </div>
                  <ChevronRight className={`h-6 w-6 ${darkMode ? 'text-slate-600' : 'text-slate-500'}`} />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Store Identifier Card */}
        {storeInfo && (
          <div className="lg:col-span-2">
            <Card className={`border-2 ${darkMode ? 'bg-gradient-to-r from-purple-900/20 to-blue-900/20 border-purple-500/50' : 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200'}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-purple-500/20' : 'bg-purple-500/15 border border-purple-500/20'}`}>
                      <Building2 className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {storeInfo.name}
                      </h3>
                      <p className={`text-sm ${darkMode ? 'text-slate-600' : 'text-slate-600'}`}>
                        ID de Tienda: <span className="font-mono font-semibold">{storeInfo.store_id}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {storeInfo.status === "global" ? (
                      <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0">
                        🔑 SUPER ADMIN
                      </Badge>
                    ) : (
                      <>
                        <Badge className="bg-emerald-600 text-white border-0">
                          {storeInfo.status === "active" ? "✓ Activa" : "✗ Inactiva"}
                        </Badge>
                        {storeInfo.plan && (
                          <Badge className="bg-purple-600 text-white border-0">
                            Plan: {storeInfo.plan.toUpperCase()}
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Left Column - Settings */}
        <div className="space-y-6">
          
          {/* ============ IDENTIDAD DE MARCA ============ */}
          <Card className={`border-2 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <CardHeader className={`border-b ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gradient-to-r from-purple-50 to-indigo-50 border-slate-200'}`}>
              <CardTitle className={`text-lg flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                <Building2 className="h-5 w-5 text-purple-500" />
                {t('settings.brandIdentity')}
              </CardTitle>
              <CardDescription className={darkMode ? 'text-slate-600' : 'text-slate-500'}>
                {t('settings.brandIdentity.desc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              
              {/* Logo Upload */}
              <div className={`p-4 rounded-xl border-2 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${darkMode ? 'bg-slate-700' : 'bg-purple-500/15 border border-purple-500/20'}`}>
                    <ImageIcon className={`h-6 w-6 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                  </div>
                  <div className="flex-1">
                    <Label className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {t('settings.logo')}
                    </Label>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-slate-600' : 'text-slate-600'}`}>
                      {t('settings.logo.desc')}
                    </p>
                    
                    {companyLogo ? (
                      <div className="mt-4 flex items-center gap-4">
                        <div className={`w-24 h-24 rounded-lg overflow-hidden border-2 ${darkMode ? 'border-slate-600 bg-slate-700' : 'border-slate-200 bg-white'}`}>
                          <img src={companyLogo} alt="Logo" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                            {t('settings.logo.change')}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={handleRemoveLogo} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                            <X className="h-4 w-4 mr-1" />
                            {t('settings.logo.remove')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className={`mt-4 p-6 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                          darkMode 
                            ? 'border-slate-600 hover:border-purple-500 hover:bg-slate-700/50' 
                            : 'border-slate-300 hover:border-purple-400 hover:bg-purple-500/10'
                        }`}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <div className="text-center">
                          <Upload className={`h-8 w-8 mx-auto mb-2 ${darkMode ? 'text-slate-500' : 'text-slate-600'}`} />
                          <p className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {t('settings.logo.upload')}
                          </p>
                          <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-600'}`}>
                            {t('settings.logo.formats')}
                          </p>
                        </div>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                  </div>
                </div>
              </div>

              {/* Language Selector */}
              <div className={`flex items-start justify-between p-4 rounded-xl border-2 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${darkMode ? 'bg-slate-700' : 'bg-blue-500/15 border border-blue-500/20'}`}>
                    <Globe className={`h-6 w-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  <div className="flex-1">
                    <Label className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {t('settings.language')}
                    </Label>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-slate-600' : 'text-slate-600'}`}>
                      {t('settings.language.desc')}
                    </p>
                  </div>
                </div>
                <Select value={language} onValueChange={handleLanguageChange}>
                  <SelectTrigger className={`w-[160px] ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="language-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className={darkMode ? 'bg-slate-800 border-slate-700' : ''}>
                    <SelectItem value="es" className={darkMode ? 'text-white hover:bg-slate-700' : ''}>
                      🇪🇸 {t('settings.language.es')}
                    </SelectItem>
                    <SelectItem value="en" className={darkMode ? 'text-white hover:bg-slate-700' : ''}>
                      🇬🇧 {t('settings.language.en')}
                    </SelectItem>
                    <SelectItem value="ca" className={darkMode ? 'text-white hover:bg-slate-700' : ''}>
                      🏴󠁥󠁳󠁣󠁴󠁿 {t('settings.language.ca')}
                    </SelectItem>
                    <SelectItem value="fr" className={darkMode ? 'text-white hover:bg-slate-700' : ''}>
                      🇫🇷 {t('settings.language.fr')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Dark Mode Toggle */}
              <div className={`flex items-start justify-between p-4 rounded-xl border-2 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${darkMode ? 'bg-slate-700' : 'bg-indigo-500/20'}`}>
                    {darkMode ? (
                      <Moon className="h-6 w-6 text-indigo-400" />
                    ) : (
                      <Sun className="h-6 w-6 text-indigo-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <Label className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {language === 'es' ? 'Modo Oscuro' : 'Dark Mode'}
                    </Label>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-slate-600' : 'text-slate-600'}`}>
                      {language === 'es' 
                        ? 'Alterna entre el tema claro y oscuro de la aplicación' 
                        : 'Toggle between light and dark application theme'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                    {darkMode 
                      ? (language === 'es' ? 'Oscuro' : 'Dark')
                      : (language === 'es' ? 'Claro' : 'Light')
                    }
                  </span>
                  <Switch
                    checked={darkMode}
                    onCheckedChange={handleDarkModeChange}
                    data-testid="dark-mode-switch"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ============ DISEÑO DE TICKET ============ */}
          <Card className={`border-2 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <CardHeader className={`border-b ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gradient-to-r from-amber-50 to-orange-50 border-slate-200'}`}>
              <CardTitle className={`text-lg flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                <Receipt className="h-5 w-5 text-amber-500" />
                {t('settings.ticketDesign')}
              </CardTitle>
              <CardDescription className={darkMode ? 'text-slate-600' : 'text-slate-500'}>
                {t('settings.ticketDesign.desc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-5">
              
              {/* Ticket Header */}
              <div>
                <Label className={`text-sm font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {t('settings.ticket.header')}
                </Label>
                <Textarea
                  value={ticketHeader}
                  onChange={handleTicketFieldChange(setTicketHeader)}
                  placeholder={t('settings.ticket.header.placeholder')}
                  className={`mt-2 min-h-[80px] ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                />
              </div>

              {/* Ticket Footer */}
              <div>
                <Label className={`text-sm font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {t('settings.ticket.footer')}
                </Label>
                <Textarea
                  value={ticketFooter}
                  onChange={handleTicketFieldChange(setTicketFooter)}
                  placeholder={t('settings.ticket.footer.placeholder')}
                  className={`mt-2 min-h-[60px] ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                />
              </div>

              {/* Legal Terms */}
              <div>
                <Label className={`text-sm font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {t('settings.ticket.terms')}
                </Label>
                <Textarea
                  value={ticketTerms}
                  onChange={handleTicketFieldChange(setTicketTerms)}
                  placeholder={t('settings.ticket.terms.placeholder')}
                  className={`mt-2 min-h-[80px] ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                />
              </div>

              {/* Toggles */}
              <div className="grid gap-4 pt-2">
                <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                  <div>
                    <Label className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {t('settings.ticket.showDni')}
                    </Label>
                    <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                      {t('settings.ticket.showDni.desc')}
                    </p>
                  </div>
                  <Switch 
                    checked={showDniOnTicket} 
                    onCheckedChange={handleToggleChange(setShowDniOnTicket)}
                  />
                </div>

                <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                  <div>
                    <Label className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {t('settings.ticket.showVat')}
                    </Label>
                    <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                      {t('settings.ticket.showVat.desc')}
                    </p>
                  </div>
                  <Switch 
                    checked={showVatOnTicket} 
                    onCheckedChange={handleToggleChange(setShowVatOnTicket)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ============ CONFIGURACIÓN DE IVA ============ */}
          <Card className={`border-2 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <CardHeader className={`border-b ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gradient-to-r from-emerald-50 to-teal-50 border-slate-200'}`}>
              <CardTitle className={`text-lg flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                <Percent className="h-5 w-5 text-emerald-500" />
                {t('settings.vat')}
              </CardTitle>
              <CardDescription className={darkMode ? 'text-slate-600' : 'text-slate-500'}>
                {t('settings.vat.desc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-5">
              
              {/* Default VAT */}
              <div className={`p-4 rounded-xl border-2 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {t('settings.vat.default')}
                    </Label>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-slate-600' : 'text-slate-600'}`}>
                      {t('settings.vat.default.desc')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={defaultVat}
                      onChange={handleVatChange}
                      min="0"
                      max="100"
                      step="0.5"
                      className={`w-24 text-center text-lg font-bold ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`}
                    />
                    <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-700'}`}>%</span>
                  </div>
                </div>
                
                {/* Quick VAT buttons */}
                <div className="flex gap-2 mt-4">
                  {[4, 10, 21].map((rate) => (
                    <Button
                      key={rate}
                      variant={defaultVat === rate ? "default" : "outline"}
                      size="sm"
                      onClick={() => { setDefaultVat(rate); setHasChanges(true); }}
                      className={defaultVat === rate ? '' : (darkMode ? 'border-slate-600 text-slate-300' : '')}
                    >
                      {rate}%
                    </Button>
                  ))}
                </div>
              </div>

              {/* VAT Included Toggle */}
              <div className={`flex items-center justify-between p-4 rounded-xl border-2 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <div>
                  <Label className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    {t('settings.vat.included')}
                  </Label>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-slate-600' : 'text-slate-600'}`}>
                    {t('settings.vat.included.desc')}
                  </p>
                </div>
                <Switch 
                  checked={vatIncludedInPrices} 
                  onCheckedChange={handleToggleChange(setVatIncludedInPrices)}
                />
              </div>
            </CardContent>
          </Card>

          {/* ============ MONEDA OPERATIVA ============ */}
          <Card className={`border-2 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`} data-testid="currency-settings-card">
            <CardHeader className={`border-b ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gradient-to-r from-amber-50 to-yellow-50 border-slate-200'}`}>
              <CardTitle className={`text-lg flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                <Coins className="h-5 w-5 text-amber-500" />
                {t('settings.currency')}
              </CardTitle>
              <CardDescription className={darkMode ? 'text-slate-600' : 'text-slate-500'}>
                {t('settings.currency.desc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className={`p-4 rounded-xl border-2 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <Label className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {t('settings.currency.label')}
                    </Label>
                    <p className={`text-sm mt-1 font-mono ${darkMode ? 'text-slate-400' : 'text-slate-600'}`} data-testid="currency-format-example">
                      {formatCurrency(1234.56)}
                    </p>
                  </div>
                  <Select
                    value={currency}
                    onValueChange={(v) => { setCurrency(v); setHasChanges(true); }}
                  >
                    <SelectTrigger className={`w-64 ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="currency-select-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={darkMode ? 'bg-slate-800 border-slate-700' : ''}>
                      {Object.values(CURRENCIES).map((c) => (
                        <SelectItem key={c.code} value={c.code} data-testid={`currency-option-${c.code}`} className={darkMode ? 'text-white hover:bg-slate-700' : ''}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className={`text-xs mt-3 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`} data-testid="currency-note">
                  {t('settings.currency.note')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* ============ IMPRESIÓN ============ */}
          <Card className={`border-2 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <CardHeader className={`border-b ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gradient-to-r from-cyan-50 to-sky-50 border-slate-200'}`}>
              <CardTitle className={`text-lg flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                <Printer className="h-5 w-5 text-cyan-500" />
                {t('settings.print')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className={`flex items-center justify-between p-4 rounded-xl border-2 ${
                autoPrint 
                  ? (darkMode ? 'border-cyan-500/50 bg-cyan-500/10' : 'border-cyan-300 bg-cyan-500/10')
                  : (darkMode ? 'border-slate-700' : 'border-slate-200')
              }`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${autoPrint ? 'bg-cyan-500/20' : (darkMode ? 'bg-slate-700' : 'bg-slate-100')}`}>
                    <Printer className={`h-6 w-6 ${autoPrint ? 'text-cyan-500' : (darkMode ? 'text-slate-600' : 'text-slate-500')}`} />
                  </div>
                  <div className="flex-1 pr-4">
                    <Label className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {t('settings.autoPrint')}
                    </Label>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-slate-600' : 'text-slate-600'}`}>
                      {t('settings.autoPrint.desc')}
                    </p>
                  </div>
                </div>
                <Switch 
                  checked={autoPrint} 
                  onCheckedChange={handleAutoPrintChange}
                  className="data-[state=checked]:bg-cyan-500"
                />
              </div>
            </CardContent>
          </Card>

          {/* ============ HARDWARE ============ */}
          <Card className={`border-2 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`} data-testid="hardware-settings">
            <CardHeader className={`border-b ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gradient-to-r from-violet-50 to-purple-50 border-slate-200'}`}>
              <CardTitle className={`text-lg flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                <MonitorSmartphone className="h-5 w-5 text-violet-500" />
                {t('settings.hardware')}
              </CardTitle>
              <CardDescription className={darkMode ? 'text-slate-600' : 'text-slate-500'}>
                {t('settings.hardware.desc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              
              {/* ===== SECCIÓN A: ESCÁNER / PISTOLA ===== */}
              <div className={`p-4 rounded-xl border-2 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
                    <ScanBarcode className={`h-5 w-5 ${darkMode ? 'text-violet-400' : 'text-violet-600'}`} />
                  </div>
                  <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    {t('settings.scanner')}
                  </h3>
                </div>
                
                {/* Modo Escaneo Rápido */}
                <div className={`flex items-center justify-between p-3 rounded-lg ${
                  quickScanMode 
                    ? (darkMode ? 'bg-violet-500/10 border border-violet-500/30' : 'bg-violet-50 border border-violet-200')
                    : (darkMode ? 'bg-slate-700/50' : 'bg-slate-50')
                }`}>
                  <div className="flex-1 pr-4">
                    <Label className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {t('settings.scanner.quickMode')}
                    </Label>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                      {quickScanMode 
                        ? t('settings.scanner.quickMode.desc')
                        : t('settings.scanner.quickMode.disabled')
                      }
                    </p>
                  </div>
                  <Switch
                    checked={quickScanMode}
                    onCheckedChange={(value) => { setQuickScanMode(value); }}
                    className="data-[state=checked]:bg-violet-500"
                    data-testid="quick-scan-mode-switch"
                  />
                </div>

                {/* Botón Probar lector */}
                <div className="mt-3 flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setScannerTestCode(""); setScannerTestOpen(true); }}
                    className={`gap-2 ${darkMode ? 'border-violet-500/40 text-violet-300 hover:bg-violet-500/10' : 'border-violet-300 text-violet-700 hover:bg-violet-50'}`}
                    data-testid="test-scanner-btn"
                  >
                    <TestTube2 className="h-4 w-4" />
                    Probar lector
                  </Button>
                  <p className={`text-xs self-center ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                    Abre un cuadro y escanea un código para verificar el hardware.
                  </p>
                </div>
              </div>

              {/* ===== SECCIÓN B: IMPRESORA ===== */}
              <div className={`p-4 rounded-xl border-2 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 rounded-lg ${darkMode ? 'bg-cyan-500/20' : 'bg-cyan-500/20'}`}>
                    <Printer className={`h-5 w-5 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
                  </div>
                  <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    {t('settings.printer')}
                  </h3>
                </div>

                <div className="space-y-4">
                  {/* Modo de impresión — Navegador vs Térmica ESC/POS */}
                  <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                    <div className="flex-1 pr-4">
                      <Label className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        Modo de impresión
                      </Label>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        <strong>Navegador</strong>: funciona con cualquier impresora del sistema.{' '}
                        <strong>Térmica ESC/POS</strong>: requiere{' '}
                        <a href="https://qz.io/download/" target="_blank" rel="noreferrer" className="underline text-cyan-600">
                          QZ Tray
                        </a>{' '}
                        instalado; permite corte y apertura de cajón.
                      </p>
                    </div>
                    <Select
                      value={printMode}
                      onValueChange={(v) => { setPrintMode(v); }}
                    >
                      <SelectTrigger className={`w-[220px] ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="print-mode-selector">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={darkMode ? 'bg-slate-800 border-slate-700' : ''}>
                        <SelectItem value="browser">Navegador (universal)</SelectItem>
                        <SelectItem value="escpos">Térmica ESC/POS (QZ Tray)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* ESC/POS printer picker — only when escpos mode active */}
                  {printMode === 'escpos' && (
                    <div className={`p-3 rounded-lg border ${
                      qzStatus === 'ok'
                        ? (darkMode ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-emerald-50 border-emerald-200')
                        : qzStatus === 'missing'
                          ? (darkMode ? 'bg-amber-500/10 border-amber-500/40' : 'bg-amber-50 border-amber-200')
                          : (darkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-slate-50 border-slate-200')
                    }`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <Label className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                            Impresora térmica
                          </Label>
                          <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {qzStatus === 'ok' && `QZ Tray conectado — ${qzPrinters.length} impresora(s) detectada(s)`}
                            {qzStatus === 'missing' && 'QZ Tray no detectado. La impresión caerá al modo Navegador.'}
                            {qzStatus === 'detecting' && 'Detectando QZ Tray…'}
                            {qzStatus === 'idle' && 'Aún no probado. Pulsa "Detectar QZ".'}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={detectQZ}
                          disabled={qzStatus === 'detecting'}
                          data-testid="qz-detect-btn"
                          className="whitespace-nowrap"
                        >
                          {qzStatus === 'detecting' ? 'Detectando…' : 'Detectar QZ'}
                        </Button>
                      </div>
                      {qzStatus === 'ok' && qzPrinters.length > 0 && (
                        <div className="mt-3">
                          <Select
                            value={qzPrinterName || ''}
                            onValueChange={(v) => setQzPrinterName(v)}
                          >
                            <SelectTrigger className={`w-full ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="qz-printer-selector">
                              <SelectValue placeholder="Elige impresora" />
                            </SelectTrigger>
                            <SelectContent className={darkMode ? 'bg-slate-800 border-slate-700' : ''}>
                              {qzPrinters.map((p) => (
                                <SelectItem key={p} value={p}>{p}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Ancho de Papel */}
                  <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                    <div className="flex-1 pr-4">
                      <Label className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {t('settings.printer.paperWidth')}
                      </Label>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                        {t('settings.printer.paperWidth.desc')}
                      </p>
                    </div>
                    <Select
                      value={paperWidth}
                      onValueChange={(value) => { setPaperWidth(value); }}
                    >
                      <SelectTrigger className={`w-[160px] ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : ''}`} data-testid="paper-width-selector">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={darkMode ? 'bg-slate-800 border-slate-700' : ''}>
                        <SelectItem value="80mm" className={darkMode ? 'text-white hover:bg-slate-700' : ''}>
                          {t('settings.printer.80mm')}
                        </SelectItem>
                        <SelectItem value="58mm" className={darkMode ? 'text-white hover:bg-slate-700' : ''}>
                          {t('settings.printer.58mm')}
                        </SelectItem>
                        <SelectItem value="A4" className={darkMode ? 'text-white hover:bg-slate-700' : ''}>
                          A4 (210mm)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Auto-Imprimir */}
                  <div className={`flex items-center justify-between p-3 rounded-lg ${
                    autoPrintOnPayment 
                      ? (darkMode ? 'bg-cyan-500/10 border border-cyan-500/30' : 'bg-cyan-500/10 border border-cyan-200')
                      : (darkMode ? 'bg-slate-700/50' : 'bg-slate-50')
                  }`}>
                    <div className="flex-1 pr-4">
                      <Label className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {t('settings.printer.autoPrint')}
                      </Label>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                        {t('settings.printer.autoPrint.desc')}
                      </p>
                    </div>
                    <Switch
                      checked={autoPrintOnPayment}
                      onCheckedChange={(value) => { setAutoPrintOnPayment(value); }}
                      className="data-[state=checked]:bg-cyan-500"
                      data-testid="auto-print-on-payment-switch"
                    />
                  </div>

                  {/* Imprimir Doble Copia */}
                  <div className={`flex items-center justify-between p-3 rounded-lg ${
                    printDoubleCopy 
                      ? (darkMode ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-amber-500/10 border border-amber-200')
                      : (darkMode ? 'bg-slate-700/50' : 'bg-slate-50')
                  }`}>
                    <div className="flex items-start gap-3 flex-1 pr-4">
                      <Copy className={`h-4 w-4 mt-0.5 ${printDoubleCopy ? 'text-amber-500' : (darkMode ? 'text-slate-500' : 'text-slate-600')}`} />
                      <div>
                        <Label className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                          {t('settings.printer.doubleCopy')}
                        </Label>
                        <p className={`text-xs mt-1 ${darkMode ? 'text-slate-600' : 'text-slate-500'}`}>
                          {t('settings.printer.doubleCopy.desc')}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={printDoubleCopy}
                      onCheckedChange={(value) => { setPrintDoubleCopy(value); }}
                      className="data-[state=checked]:bg-amber-500"
                      data-testid="print-double-copy-switch"
                    />
                  </div>

                  {/* Botón Imprimir ticket de prueba */}
                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrintTestTicket}
                      className={`gap-2 ${darkMode ? 'border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10' : 'border-cyan-300 text-cyan-700 hover:bg-cyan-50'}`}
                      data-testid="print-test-ticket-btn"
                    >
                      <FileText className="h-4 w-4" />
                      Imprimir ticket de prueba
                    </Button>
                    <p className={`text-xs self-center ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                      Envía un recibo dummy a la impresora por defecto ({paperWidth}).
                    </p>
                  </div>

                  {/* ── Live ticket preview ─────────────────────────────
                       Rendered in a sandboxed iframe so its @page /
                       @media print CSS cannot escape into the app. Reacts
                       to paperWidth, header, footer, terms and logo. */}
                  <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-700/40 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <Label className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        Vista previa del ticket
                      </Label>
                      <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Ancho: <strong>{paperWidth}</strong> — se actualiza al cambiar los ajustes
                      </span>
                    </div>
                    <div
                      className={`rounded-md ${darkMode ? 'bg-slate-900' : 'bg-white'} flex justify-center overflow-auto`}
                      style={{ maxHeight: 520, padding: 12 }}
                      data-testid="ticket-live-preview"
                    >
                      <iframe
                        key={`preview-${paperWidth}`}
                        title="Vista previa del ticket"
                        srcDoc={previewSrcDoc}
                        sandbox="allow-same-origin"
                        className="rounded-md shadow-md border border-slate-200"
                        style={{
                          // Match preview width to the selected paper.
                          width: paperWidth === 'A4' ? 320 : (paperWidth === '58mm' ? 220 : 300),
                          height: 480,
                          background: '#fff',
                        }}
                      />
                    </div>
                  </div>

                  {/* Instrucciones de Configuración */}
                  <div className={`p-4 rounded-lg border-2 border-dashed ${darkMode ? 'bg-slate-700/30 border-slate-600' : 'bg-blue-500/10 border-blue-200'}`}>
                    <div className="flex items-start gap-3">
                      <div className={`p-1.5 rounded-full ${darkMode ? 'bg-blue-500/20' : 'bg-blue-500/15'}`}>
                        <AlertCircle className={`h-4 w-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                      </div>
                      <div>
                        <p className={`font-medium text-sm ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                          {t('settings.printer.setupInstructions.title')}
                        </p>
                        <ul className={`mt-2 space-y-1.5 text-xs ${darkMode ? 'text-slate-600' : 'text-slate-600'}`}>
                          <li className="flex items-start gap-2">
                            <span className="text-green-500 font-bold">1.</span>
                            <span>{t('settings.printer.setupInstructions.step1')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-green-500 font-bold">2.</span>
                            <span>{t('settings.printer.setupInstructions.step2')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-green-500 font-bold">3.</span>
                            <span>{t('settings.printer.setupInstructions.step3')}</span>
                          </li>
                        </ul>
                        <p className={`mt-3 text-xs italic ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                          {t('settings.printer.setupInstructions.note')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ═════════════════════════════════════════════════════════════
              Personalizar menú (per-user cosmetic sidebar filter)
              ─────────────────────────────────────────────────────────
              Shows a checkbox for every menu item the current user CAN
              access by role. Unchecking removes it from the sidebar.
              Purely cosmetic — the routes stay reachable by URL and the
              role-based access controls always win. Dashboard and
              Configuración cannot be hidden (safety hatch back home).
          ═════════════════════════════════════════════════════════════ */}
          <Card className={`border-2 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`} data-testid="menu-preferences-card">
            <CardHeader className={`border-b ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gradient-to-r from-purple-50 to-blue-50 border-slate-200'}`}>
              <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                <LayoutList className="h-5 w-5 text-purple-500" />
                Personalizar menú
              </CardTitle>
              <CardDescription className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
                Marca los ítems que quieres ver en la barra lateral. Desmarcar sólo los oculta del menú — la URL sigue siendo accesible y no cambia tus permisos.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {SECTION_ORDER.map((sec) => {
                const items = MENU_ITEMS.filter(
                  (it) => it.section === sec && isItemAllowedForRole(it, user?.role)
                );
                if (items.length === 0) return null;
                return (
                  <div key={sec}>
                    <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                      {SECTION_LABELS[sec]}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {items.map((it) => {
                        const isHidden = (user?.hidden_menu_items || []).includes(it.to);
                        const disabled = !!it.alwaysVisible;
                        return (
                          <label
                            key={it.to}
                            className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer transition-colors ${
                              disabled
                                ? (darkMode ? 'bg-slate-700/30 opacity-60 cursor-not-allowed' : 'bg-slate-50 opacity-60 cursor-not-allowed')
                                : (darkMode ? 'hover:bg-slate-700/60' : 'hover:bg-slate-50')
                            }`}
                          >
                            <Checkbox
                              checked={!isHidden}
                              disabled={disabled}
                              onCheckedChange={(checked) => {
                                const cur = new Set(user?.hidden_menu_items || []);
                                if (checked) cur.delete(it.to);
                                else cur.add(it.to);
                                updateMenuPreferences(Array.from(cur)).catch(() => {
                                  toast.error("No se pudo guardar la preferencia del menú");
                                });
                              }}
                              data-testid={`menu-pref-${it.to.replace(/[^a-z0-9]/gi, '') || 'dashboard'}`}
                            />
                            <span className={`text-sm ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                              {it.labelEs}
                              {disabled && (
                                <span className={`ml-2 text-[10px] uppercase font-semibold ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                  fijo
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="pt-2 border-t border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
                <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                  Los cambios se guardan al instante y se aplican al recargar la barra lateral.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateMenuPreferences([]).then(() => toast.success("Menú restaurado")).catch(() => toast.error("No se pudo restaurar"));
                  }}
                  data-testid="menu-pref-reset-btn"
                >
                  Restaurar todo
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Ticket Preview */}
        <div className="lg:sticky lg:top-24 h-fit">
          <Card className={`border-2 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            <CardHeader className={`border-b ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gradient-to-r from-slate-100 to-slate-50 border-slate-200'}`}>
              <CardTitle className={`text-lg flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                <Eye className="h-5 w-5 text-slate-500" />
                {t('settings.ticket.preview')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Ticket Preview */}
              <div className="bg-white text-black rounded-lg shadow-lg p-4 mx-auto max-w-[300px] font-mono text-xs leading-relaxed border border-slate-300">
                
                {/* Logo */}
                {companyLogo && (
                  <div className="flex justify-center mb-3 pb-3 border-b border-dashed border-slate-300">
                    <img src={companyLogo} alt="Logo" className="h-12 object-contain" />
                  </div>
                )}
                
                {/* Header */}
                <div className="text-center whitespace-pre-wrap mb-3 pb-3 border-b border-dashed border-slate-300">
                  {ticketHeader || 'NOMBRE DE TU NEGOCIO'}
                </div>
                
                {/* Ticket Title */}
                <div className="text-center font-bold text-sm mb-3">
                  {t('ticket.rental')}
                </div>
                
                {/* Date */}
                <div className="flex justify-between mb-1">
                  <span>{t('ticket.date')}:</span>
                  <span>{sampleTicket.date}</span>
                </div>
                
                {/* Customer */}
                <div className="flex justify-between mb-1">
                  <span>{t('ticket.customer')}:</span>
                  <span className="text-right max-w-[150px] truncate">{sampleTicket.customer}</span>
                </div>
                
                {/* DNI - Conditional */}
                {showDniOnTicket && (
                  <div className="flex justify-between mb-1">
                    <span>{t('ticket.dni')}:</span>
                    <span>{sampleTicket.dni}</span>
                  </div>
                )}
                
                {/* Items */}
                <div className="my-3 py-3 border-t border-b border-dashed border-slate-300">
                  <div className="font-bold mb-2">{t('ticket.items')}:</div>
                  {sampleTicket.items.map((item, idx) => (
                    <div key={item.id || item.name || `ti-${idx}`} className="flex justify-between">
                      <span className="max-w-[180px] truncate">{item.name}</span>
                      <span>{formatCurrency(item.price)}</span>
                    </div>
                  ))}
                </div>
                
                {/* VAT Breakdown - Conditional */}
                {showVatOnTicket ? (
                  <div className="mb-3 space-y-1">
                    <div className="flex justify-between">
                      <span>{t('ticket.subtotal')}:</span>
                      <span>{formatCurrency(vatCalc.base)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('ticket.vat')} ({defaultVat}%):</span>
                      <span>{formatCurrency(vatCalc.vat)}</span>
                    </div>
                  </div>
                ) : null}
                
                {/* Total */}
                <div className="flex justify-between font-bold text-base border-t border-double border-slate-400 pt-2 mt-2">
                  <span>{t('ticket.total')}:</span>
                  <span>{formatCurrency(showVatOnTicket ? vatCalc.total.toFixed(2) : sampleTicket.total)}</span>
                </div>
                
                {/* VAT Included Note */}
                {!showVatOnTicket && vatIncludedInPrices && (
                  <div className="text-center text-[10px] text-slate-500 mt-1">
                    ({t('ticket.vatIncluded')})
                  </div>
                )}
                
                {/* Payment Method */}
                <div className="flex justify-between mt-3 pt-2 border-t border-dashed border-slate-300">
                  <span>{t('ticket.paymentMethod')}:</span>
                  <span>{t('ticket.cash')}</span>
                </div>
                
                {/* Footer */}
                <div className="text-center whitespace-pre-wrap mt-4 pt-3 border-t border-dashed border-slate-300">
                  {ticketFooter || t('ticket.thankYou')}
                </div>
                
                {/* Legal Terms */}
                {ticketTerms && (
                  <div className="mt-3 pt-3 border-t border-dashed border-slate-300 text-[9px] text-slate-500 text-center">
                    {ticketTerms}
                  </div>
                )}
              </div>
              
              {/* Preview Legend */}
              <div className={`mt-4 text-center text-xs ${darkMode ? 'text-slate-500' : 'text-slate-600'}`}>
                Los cambios se reflejan automáticamente
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ──────────────────────────────────────────────────────────
          Scanner test dialog — captures the next scan from ANY HID
          reader (USB or bluetooth) and displays it so the operator
          knows the hardware works in seconds.
      ────────────────────────────────────────────────────────── */}
      <Dialog open={scannerTestOpen} onOpenChange={setScannerTestOpen}>
        <DialogContent data-testid="scanner-test-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanBarcode className="h-5 w-5 text-violet-500" />
              Probar lector de códigos
            </DialogTitle>
            <DialogDescription>
              Escanea CUALQUIER código de barras con tu lector. Debe aparecer aquí en menos de un segundo.
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <div
              className={`rounded-lg border-2 border-dashed p-6 text-center font-mono text-lg break-all ${
                scannerTestCode
                  ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                  : "border-slate-300 bg-slate-50 text-slate-400 animate-pulse"
              }`}
              data-testid="scanner-test-output"
            >
              {scannerTestCode || "Esperando escaneo…"}
            </div>
            {scannerTestCode && (
              <p className="mt-3 text-center text-sm text-emerald-600 font-semibold">
                ✓ El lector funciona correctamente
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScannerTestOpen(false)} data-testid="scanner-test-close-btn">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
